import * as yup from 'yup';
import { Check, ChevronRight } from 'react-feather';
import { FormManager, useForm } from 'react-form-state-manager';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

import { gql } from '__generated__';
import config from '../config';

import { emailRegex, getServerErrors, setWindowLocation } from '../../common/helpers';
import { useRequestLogin, useValidateUserEmail } from '../../common/Auth/useAuth';
import SessionStorage from '../../common/SessionStorage';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useScroll from '../../common/useScroll';
import useValidation from '../../common/useValidation';

interface SignupFormData {
  email: string;
  promo?: string;
}

export const AddUserMutation = gql(`
  mutation AddUser($input: AddUserInput!) {
    addUser(input: $input)
  }
`);

const requestLoginSchema = yup.object().shape({
  email: yup.string().required().matches(emailRegex, { excludeEmptyString: true, name: 'email' }),
});

const signupSchema = yup.object().shape({
  email: yup.string().required().matches(emailRegex, { name: 'email' }),
  first_name: yup.string().required(),
  last_name: yup.string().required(),
});

export interface SignupFormProps {
  redirect?: typeof setWindowLocation;
}

const SignupForm = ({ redirect = setWindowLocation }: SignupFormProps) => {
  const getPromo = () => {
    const promo = SessionStorage.getItem('promo');

    if (typeof promo === 'string') {
      return promo;
    }

    return null;
  };

  const form = useForm<SignupFormData>({
    values: {
      email: '',
    },
  });

  return (
    <UI.Card sc={{ padding: [4, 6], mx: [3, 4] }}>
      <Switch>
        <Route path="/register/2">
          <RegisterForm email={form.values.email} promo={getPromo()} redirect={redirect} />
        </Route>

        <Route path="/register">
          <RequestLoginForm form={form} redirect={redirect} />
        </Route>
      </Switch>
    </UI.Card>
  );
};

interface RequestLoginFormProps {
  form: FormManager<SignupFormData>;
  redirect: typeof setWindowLocation;
}

const RequestLoginForm = ({ form, redirect }: RequestLoginFormProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const history = useHistory();

  const [requestLogin, { error, loading }] = useRequestLogin(form.values.email, {
    onCompleted: (loginRequestedForEmail: string) => {
      if (loginRequestedForEmail !== null) {
        // The user exists but has chosen to click on the 'register' link,
        // so we redirect them to the place where they can create a new event.
        const returnUrl = `${config.baseUrl}/b/projects/add`;
        redirect(`/login?returnUrl=${encodeURIComponent(returnUrl)}&locale=${locale}&loginRequestedForEmail=${encodeURIComponent(loginRequestedForEmail)}`);
      } else {
        // The user does not exist so we need more info.
        history.push(`/register/2?locale=${locale}`);
      }
    },
  });

  const { valid, errors } = useValidation({
    schema: requestLoginSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const { userExists, loading: validationLoading } = useValidateUserEmail(form.values.email, {
    skip: !valid,
  });

  const [type, setType] = useState<'participant' | 'organiser' | null>(null);
  const isParticipant = type === 'participant';
  const shouldSelectType = valid && !validationLoading;

  return (
    <UI.Form onSubmit={() => requestLogin()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H3 sc={{ mb: 1 }}>
            {t('create_an_account')}
          </UI.H3>
          {t('create_account_description')}
        </UI.Div>
        <UI.InputGroup sc={{ valid: !errors.email, touched: form.touched.email }}>
          <UI.InputLabel htmlFor="EmailAddress">
            {t('email_address')}
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('email')}
            inputMode="email"
            placeholder={t('email_placeholder')}
            sc={{ size: 'lg' }}
            id="EmailAddress"
            autoFocus
          />
          <UI.ErrorMessages attribute={t('email_address')} errors={errors.email} />
        </UI.InputGroup>

        {shouldSelectType && (
          <>
            <UI.InputGroup>
              <UI.InputLabel>
                {t('are_you_organiser_or_participant')}
              </UI.InputLabel>
              <UI.Radio
                onChange={() => setType('organiser')}
                checked={type === 'organiser'}
              >
                {t('organiser')}
              </UI.Radio>
              <UI.Radio
                onChange={() => setType('participant')}
                checked={type === 'participant'}
              >
                {t('participant')}
              </UI.Radio>
            </UI.InputGroup>

            {isParticipant && (
              <UI.Info>
                {t('no_organiser_account_as_participant')}
              </UI.Info>
            )}
          </>
        )}

        {(!isParticipant || (userExists && !shouldSelectType)) && (
          <UI.Button
            type="submit"
            sc={{ brand: 'primary', block: true, size: 'lg', loading }}
            disabled={!valid || (shouldSelectType && type === null) || loading}
          >
            <UI.Strong sc={{ mr: 1 }}>
              {t(userExists ? 'login' : 'continue')}
            </UI.Strong>
            {' '}
            <UI.Icon>
              <ChevronRight strokeWidth={2.5} />
            </UI.Icon>
          </UI.Button>
        )}
      </UI.FormGrid>
    </UI.Form>
  );
};

interface RegisterFormProps {
  email: string;
  promo?: string;
  redirect: typeof setWindowLocation;
}

const RegisterForm = ({ email, promo, redirect }: RegisterFormProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { scrollTo } = useScroll();

  const form = useForm({
    values: {
      first_name: '',
      last_name: '',
      email,
      promo,
    },
  });

  const [addUser, { data, error, loading }] = useMutation(AddUserMutation, {
    variables: {
      input: {
        ...form.values,
        locale,
      },
    },
    onCompleted: ({ addUser: email }) => {
      const returnUrl = encodeURIComponent(`${config.baseUrl}/b/projects/add`);
      redirect(`/login?returnUrl=${returnUrl}&locale=${locale}&loginRequestedForEmail=${encodeURIComponent(email)}`);
    },
    onError: () => {
      scrollTo('SignUpForm', { gutter: 1 });
    },
  });

  const { valid, errors } = useValidation({
    schema: signupSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  useEffect(() => {
    if (errors.promo) {
      form.delete('promo');
    }
  }, [form, errors?.promo]);

  if (!form.values.email) {
    return <Redirect to={`/register?locale=${locale}`} />;
  }

  return (
    <UI.Form onSubmit={() => addUser()} id="SignUpForm">
      <UI.FormGrid>
        <UI.Div>
          <UI.H3 sc={{ mb: 1 }}>
            {t('create_an_account')}
          </UI.H3>
          {t('create_account_description')}
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.InputGroup>
          <UI.InputLabel htmlFor="EmailAddress">
            {t('email_address')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput {...form.text('email')} disabled sc={{ size: 'lg' }} maxLength={255} id="EmailAddress" />
          <UI.ErrorMessages attribute={t('email_address')} errors={errors.email} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.first_name, touched: form.touched.first_name }}>
          <UI.InputLabel htmlFor="FirstName">
            {t('first_name')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('first_name')}
            autoFocus
            sc={{ size: 'lg' }}
            maxLength={255}
            id="FirstName"
          />
          <UI.ErrorMessages attribute={t('first_name')} errors={errors.first_name} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.last_name, touched: form.touched.last_name }}>
          <UI.InputLabel htmlFor="LastName">
            {t('last_name')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput {...form.text('last_name')} sc={{ size: 'lg' }} maxLength={255} id="LastName" />
          <UI.ErrorMessages attribute={t('last_name')} errors={errors.last_name} />
        </UI.InputGroup>

        {form.values.promo && (
          <UI.Info sc={{ small: true }}>
            <UI.Icon sc={{ color: 'primary.600', mr: 1 }}>
              <Check />
            </UI.Icon>
            <Trans
              i18nKey="activated_promo"
              values={{ promo: form.values.promo }}
            >
              <UI.Strong />
            </Trans>
          </UI.Info>
        )}

        <UI.Div sc={{ small: true }}>
          <Trans i18nKey="sign_up_agree_to_terms_of_service">
            <UI.A href={`https://about.atleta.cc/${locale}/terms-and-conditions`} target="_blank" />
          </Trans>
        </UI.Div>

        <UI.FormGrid sc={{ gutter: 0.5 }}>
          <UI.Button
            type="submit"
            sc={{ brand: 'primary', block: true, size: 'lg', loading: !!data || loading }}
            disabled={!!data || loading || !valid}
          >
            {t('create_account')}
          </UI.Button>

          <UI.LinkButton to={`/register?locale=${locale}`} sc={{ block: true, disabled: !!data || loading }}>
            {t('cancel')}
          </UI.LinkButton>
        </UI.FormGrid>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default SignupForm;
